import {
  Box,
  Breadcrumb,
  Button,
  ChevronEndMediumIcon,
  Flex,
  Divider,
  ProviderConsumer as FluentUIThemeConsumer,
  Skeleton,
  ArrowLeftIcon,
  EditIcon
} from '@fluentui/react-northstar';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { EditTicketActions } from './EditTicketActions';
import { threedigitized, toolbarButtonColor } from '../../tikit/ticketHelper';
import AppCSS from '../../App.module.css';
import { useTranslation } from 'react-i18next';
import { RefreshIcon } from './RefreshIcon';

export interface BreadCrumbItemProps {
  title: string;
  link: string;
}

interface ETTProps {
  onInteraction?: (interaction: any) => void;
  ticketData: Ticket;
  ticketId: number;
  currentUserId?: number;
  disabledSave: boolean;
  breadcrumbItems: BreadCrumbItemProps[];
  edittingState: boolean;
  isItemSelected?: boolean;
  isUserLicensed: boolean;
  mergedTicketIds: number[];
}

export const EditTicketToolbar = (props: ETTProps) => {
  const {t} = useTranslation();
  const history = useHistory();

  const onSave = () => {
    if (props.onInteraction) props.onInteraction({ event: 'saveTicket' });
  };

  const actions = action => {
    props.onInteraction({ event: action.event, data: action.data });
  };

  return (
    <FluentUIThemeConsumer
      render={globalTheme => (
        <Flex
          vAlign="center"
          style={{ height: '48px' }}
          className={`py-2`}
        >
          <Flex.Item grow>
            <Flex>
              <Box>
                <div className={`hidden md:flex`}>
                  <Breadcrumb
                    aria-label="breadcrumb"
                    style={{ height: '48px', marginRight: '.5rem', marginLeft: '.75rem' }}
                  >
                    {props.breadcrumbItems.map((v, _i) => {
                      let clickLink = e => {
                        e.preventDefault();
                        history.push(v.link);
                      };

                      return (
                        <Breadcrumb.Item>
                          <Breadcrumb.Link
                            {...(v.link !== null && {
                              ...{
                                onClick: clickLink,
                                href: '/',
                                style: { cursor: 'pointer' }
                              }
                            })}
                          >
                            {v.title}
                          </Breadcrumb.Link>
                        </Breadcrumb.Item>
                      );
                    })}
                    <Breadcrumb.Divider>
                      <ChevronEndMediumIcon />
                    </Breadcrumb.Divider>
                    <Breadcrumb.Item aria-current="page">
                      <strong>
                        {t('ticket-details.toolbar.edit-ticket')} #{threedigitized(props.ticketId)}
                      </strong>
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </div>
                <div className={`md:hidden action-button pt-2`}>
                  <Flex vAlign="center">
                    <Button
                      onClick={() => {
                        history.goBack();
                      }}
                      className={`mb-1`}
                      content={<ArrowLeftIcon size="large" />}
                      text
                      iconOnly
                      style={{
                        color:
                          globalTheme.siteVariables.colorScheme.brand
                            .foregroundActive
                      }}
                    />
                  </Flex>
                </div>
              </Box>
              <Box>
                <Divider vertical />
              </Box>
              {props.currentUserId != null && !props.ticketData.Closed && (
                <>
                  {!props.isItemSelected ? (
                    <Box className={`flex-grow`}>
                      <EditTicketActions
                        ticketData={props.ticketData}
                        ticketId={props.ticketId}
                        edittingState={props.edittingState}
                        currentUserId={props.currentUserId}
                        onInteraction={actions}
                        mergedTicketIds={props.mergedTicketIds}
                        isUserLicensed={props.isUserLicensed}
                      />
                    </Box>
                  ) : (
                    <Box className={`flex-grow`}>
                      <div className={`mt-3 ml-2`}>
                        <Skeleton animation="wave">
                          <Skeleton.Line width="20%" height="24px" />
                        </Skeleton>
                      </div>
                    </Box>
                  )}
                </>
              )}
              {props.currentUserId != null && props.ticketData.Closed && !props.isItemSelected && !props.edittingState && (
                // A closed ticket drops the toolbar's actions, but on a phone the pencil is the only way
                // into the properties - and Status, the one property a closed ticket still allows, is how
                // it is reopened. So the pencil stays, as the open ticket draws it (phone-only there too).
                <Box className={`flex-grow`}>
                  <div className={`flex items-center`} style={{ height: '48px' }}>
                    {/* A closed ticket has no other actions, but can still be loaded again (see EditTicketActions). */}
                    <Button
                      icon={<RefreshIcon />}
                      text
                      onClick={() => props.onInteraction?.({ event: 'refresh' })}
                      content={t('ticket.toolbar.refresh')}
                      title={t('ticket.toolbar.refresh')}
                      size="small"
                      style={{ color: toolbarButtonColor(true, globalTheme.siteVariables.colorScheme) }}
                      className='hide-text'
                    />
                    <div className={`flex-grow`} />
                    <Divider vertical className={`lg-hidden-important`} />
                    <Button
                      disabled={!props.isUserLicensed}
                      text
                      icon={<EditIcon size="medium" />}
                      onClick={() => props.onInteraction?.({ event: 'onEdit' })}
                      title={t('ticket-details.toolbar.action.edit')}
                      size="medium"
                      iconOnly
                      className={`${AppCSS.navActionButton} mr-2 ml-1 lg-hidden-important`}
                      style={{ color: toolbarButtonColor(props.isUserLicensed, globalTheme.siteVariables.colorScheme) }}
                    />
                  </div>
                </Box>
              )}
            </Flex>
          </Flex.Item>
        </Flex>
      )}
    />
  );
};
